import './style.css';
import {
  useEffect,
  useState,
  useRef,
  useMemo
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { last, matches } from 'lodash';
import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { getAllChildDevicesByType, getDeviceUrl } from 'util/device';
import useDevicesStore from 'store/devices';
import useAlarmsStore from 'store/alarms';
import useAlarmTypesStore from 'store/alarm-types';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import {
  confirm,
  createModal,
  useNotificationsStore,
  Banner,
  DateRangePicker,
  LoadingIndicator,
  RichTable,
  Select,
  Tooltip
} from '@thermon-platforms/thermon-ui-core';
import AlarmsTableRow from './alarms-table-row';
import BulkAddNoteModal from './bulk-add-note-modal';
import api from 'util/api';
import { getConfigItem } from 'util/device';
import { printNotesForMultipleDevices } from 'components/notes/util';


/**
 * @module view/Alarms
 */

/**
 * @function Alarms
 * @memberof module:view/Alarms
 * @returns {ReactElement}
 */
function Alarms() {
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [lockedOutDevices, setLockedOutDevices] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [acknowledging, setAcknowledging] = useState([]);
  const [, notificationsStoreActions] = useNotificationsStore();
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [alarmsStore, alarmsStoreActions] = useAlarmsStore();
  const [alarmTypesStore, alarmTypesStoreActions] = useAlarmTypesStore();
  const [, usersStoreActions] = useUsersStore();
  const [rolesStore] = useRolesStore();

  useEffect(() => {
    if (!devicesStore.devices.length && !devicesStore.loading) {
      devicesStoreActions.query();
    }

    if (!alarmTypesStore.types.length && !alarmTypesStore.loading) {
      alarmTypesStoreActions.query();
    }

    alarmsStoreActions.query();
  }, []);

  useEffect(() => {
    if (devicesStore.loading || alarmsStore.loading) {
      return setLoading(true);
    }

    const lockedOut = [];
    devicesStore.shallowDevices.forEach(device => {
      if (getConfigItem(device, 'LockoutTagOut')?.value == 'on') lockedOut.push(device);
    });
    setLockedOutDevices(lockedOut);

    setLoading(false);
  }, [devicesStore.loading, alarmsStore.loading]);

  // Map of alarm type name to display name for easy lookup in rows
  const nameMap = useMemo(
    () => Object.fromEntries(
      alarmTypesStore.types.map(type => [type.name, type.displayName])
    ),
    [alarmTypesStore.types]
  );

  useEffect(() => {
    setFilteredRows(alarmFilter(alarmsStore.alarms).map(row =>
      // add displayName to each row so sorting works correctly
      ({
        ...row,
        displayName: nameMap[row.alarmType] || row.alarmType,
        deviceName: devicesStore.shallowDevices.get(row.deviceId)?.model?.name || ''
      }))
    );
  }, [alarmsStore.alarms, searchParams, nameMap]);

  function alarmFilter(alarms) {
    const search = {};
    const dateRange = {};
    let locationId;
    let deviceName;
    // We need to slice and dice the filters a little since
    // some of them to don't apply 1:1 to alatm models
    searchParams.forEach((value, key) => {
      // Alarms have a date and we want to see if it's between
      // the user selected dates
      if (key == 'start' || key == 'end') {
        dateRange[key] = value;
        return;
      }
      // Alarms can be generated by a device in a specific location
      // but location isn't defined on the model
      if (key == 'locationId') {
        locationId = value;
        return;
      }
      // Device name is not on the alarm model but we want to be able to filter by it
      if (key == 'deviceName') {
        deviceName = value;
        return;
      }
      // Search params encode booleans as strings so let's manually
      // convert them so our match function works correctly
      if (value == 'true') {
        search[key] = true;
        return;
      }
      if (value == 'false') {
        search[key] = false;
        return;
      }
      if (!value) return;
      search[key] = value;
    });

    return alarms.filter(alarm => {
      // Check our alarm for fitting between the selected dates (if applicable)
      if (dateRange.start && alarm.createdAt < dateRange.start) return false;
      if (dateRange.end && alarm.createdAt > dateRange.end) return false;
      if (deviceName) {
        const device = devicesStore.shallowDevices.get(alarm.deviceId);
        if (device && !device.model.name.toLowerCase().includes(deviceName.toLowerCase())) {
          return false;
        }
      }
      // Similarly, let's check if the device is a child of the selected location
      if (locationId) {
        const device = devicesStore.shallowDevices.get(alarm.deviceId);
        if (device && !device.path.some(d => d.id == locationId)) {
          return false;
        }
      }
      // Finally, fall through to our default match function
      return matches(search)(alarm);
    });
  }

  /**
   * clear existing search
   * @function resetSearch
   * @memberof module:view/Alarms
   */
  const datePicker = useRef();
  const inputEls = useRef([]);
  function resetSearch() {
    setSearchParams({});
    datePicker.current.reset(false);
    inputEls.current.forEach(el => el.reset(false));
  }

  /**
   * Download all alarm data as a CSV
   * @function download
   * @memberof module:view/Alarms
   */
  function download() {
    api('/alarms/GetAlarmsDashboardData')
      .then(async res => {
        const data = await res.text();
        const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'alarm_data.csv';
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        notificationsStoreActions.notify(
          t`An error occurred downloading alarm data.`,
          'alert'
        );
      });
  }

  /**
   * Add an alarm to be staged for acknowledgement
   * @function acknowledge
   * @memberof module:view/Alarms
   * @param {object} alarm an alarm model
   */
  function acknowledge(alarm) {
    setAcknowledging([...acknowledging, alarm]);

    alarmsStoreActions
      .acknowledge(alarm)
      .then(() => {
        setAcknowledging([].concat(acknowledging).splice(acknowledging.indexOf(alarm), 1));
        notificationsStoreActions.notify(
          t`Acknowledged` + ` ${alarm.alarmType}`,
          'ok'
        );
      });
  }

  /**
   * Add an alarm to be staged for acknowledgement
   * @function acknowledgeAlarms
   * @memberof module:view/Alarms
   * @param {array} alarms an array of alarm models
   */
  function acknowledgeAlarms(alarms) {
    setAcknowledging([...alarms]);
    alarms.forEach(acknowledge);
  }

  /**
   * Lock Out N number of devices
   * @function lockOut
   * @memberof module:view/Alarms
   * @param {array} alarms an array of alarm models
   */
  function lockOut(alarms) {
    confirm({
      header: `${t`Are you sure you want to lock out`} ${plural(alarms.length, {
        one: "# device",
        other: "# devices",
      })}?`,
      message: t`Locking out these devices does not guarantee safety and will only suppress alarms.`
    }).then(async () => {
      const promise = Promise.all(alarms.filter(alarm => {
        return alarm.deviceId;
      }).map(alarm => {
        const device = devicesStore.shallowDevices.get(alarm.deviceId);
        return api(`/devices/${alarm.deviceId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            type: device?.model?.type,
            configuration: [{
              key: 'LockoutTagOut',
              value: 'on'
            }]
          })
        });
      }));

      promise.then(() => {
        notificationsStoreActions.notify(
          t`Lock out request was successful.`,
          'ok'
        );
      });

      promise.finally(() => { // update no matter what the outcome is
        devicesStoreActions.query();
      });
    }).catch(() => {
      /* no-op */
    });
  }

  /**
   * Add note to N number of devices
   * @function addNotes
   * @memberof module:view/Alarms
   * @param {array} alarms an array of alarm models
   */
  function addNotes(alarms) {
    const addNotesModal = createModal(<BulkAddNoteModal alarms={alarms} />);
    addNotesModal.open()
      .then((res) => {
        notificationsStoreActions.notify(
          t`Add note request was successful.`,
          'ok'
        );
      })
      .catch(/* no-op */);
  }

  function printNotes(alarms) {
    const devices = [];
    alarms.forEach(alarm => {
      const device = devicesStore.shallowDevices.get(alarm.deviceId);
      if (device && !devices.includes(device?.model)) devices.push(device?.model);
    });
    printNotesForMultipleDevices(devices);
  }

  /**
   * @function updateSearch
   * @memberof module:view/Alarms
   * @param {Event} event
   * @param {string} which search query item key
   */
  function updateSearch(value, which) {
    const search = Object.fromEntries(searchParams);

    // Update the value
    if (value !== undefined) {
      search[which] = value;
    } else {
      delete search[which];
    }

    // Call our hook to update our search
    setSearchParams(search);
  }

  /**
   * @function updateDate
   * @memberof module:view/Alarms
   * @param {object} dateRange
   */
  function updateDate(dateRange) {
    // Object.fromEntries converts the search object into a normal js object
    // Assign our existing params plus the new date to a new object and
    // update our state
    setSearchParams(Object.assign({}, Object.fromEntries(searchParams), dateRange));
  }

  const columns = [
    {
      name: t`Alarm Type`,
      key: 'displayName',
      sortable: true
    },
    {
      name: t`Reading`,
      key: 'reading'
    },
    {
      name: t`Set Point`,
      key: 'setpoint'
    },
    {
      name: t`Device`,
      key: 'deviceName',
      sortable: true
    },
    {
      name: t`Created At`,
      sortable: true,
      key: 'createdAt'
    },
    {
      name: t`Updated At`,
      sortable: true,
      key: 'lastUpdatedAt'
    },
    {
      name: t`Latest Note`
    },
    {
      name: t`Actions`,
      key: 'Actions'
    }
  ];

  function Controls() {
    return (
      <div className="table-controls">
        <button
          className="download"
          onClick={download}
          disabled={!alarmsStore.alarms.length}
          data-tooltip-content={t`Download Alarm Data`}
          data-tooltip-id="control-tooltip"
        >
          <Trans>Download Alarm Data</Trans>
        </button>
        <button
          className={`acknowledge ${acknowledging.length ? 'processing' : ''}`}
          onClick={() => acknowledgeAlarms(selectedRows)}
          disabled={!selectedRows.length || acknowledging.length}
          data-tooltip-content={plural(selectedRows.length, {
            one: 'Acknowledge # Alarm',
            other: 'Acknowledge # Alarms',
          })}
          data-tooltip-id="control-tooltip"
        >
          <Trans>Acknowledge</Trans>
        </button>
        {usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles) ? <>
          <button
            className={`lock-out`}
            onClick={() => lockOut(selectedRows)}
            disabled={!selectedRows.length || acknowledging.length}
            data-tooltip-content={plural(selectedRows.length, {
              one: 'Lock Out / Tag Out # Device',
              other: 'Lock Out / Tag Out # Devices',
            })}
            data-tooltip-id="control-tooltip"
          >
            <Trans>Lock Out / Tag Out</Trans>
          </button>
        </> : null}
        <button
          className={`add-note`}
          onClick={() => addNotes(selectedRows)}
          disabled={!selectedRows.length || acknowledging.length}
          data-tooltip-content={plural(selectedRows.length, {
            one: 'Add Note to # Alarm',
            other: 'Add Note to # Alarms',
          })}
          data-tooltip-id="control-tooltip"
        >
          <Trans>Add Note</Trans>
        </button>
        <button
          className={`print`}
          onClick={() => printNotes(selectedRows)}
          disabled={!selectedRows.length || acknowledging.length}
          data-tooltip-content={plural(selectedRows.length, {
            one: 'Print Notes for # Alarm',
            other: 'Print Notes for # Alarms',
          })}
          data-tooltip-id="control-tooltip"
        >
          <Trans>Print Notes</Trans>
        </button>
        {loading && !alarmsStore?.alarms?.length ?
          <LoadingIndicator show={true}/> :
          <div className="alarm-count">
            <Trans>Showing {filteredRows.length} of {alarmsStore.alarms.length} alarms</Trans>
          </div>
        }

        <Tooltip id="control-tooltip" place="bottom-start" />
      </div>
    );
  }

  return (
    <div className="view alarms">
      <div className="sidebar">
        <h3><Trans>Filters</Trans></h3>
        <Select
          disabled={loading && !alarmsStore.alarms.length}
          placeholder="Alarm Type"
          onChange={event => updateSearch(event, 'alarmType')}
          value={searchParams.get('alarmType') || undefined}
          options={alarmTypesStore.types.map((type) => {
            return {
              label: type.displayName,
              value: type.name
            }
          })}
          ref={el => (inputEls.current[0] = el)}
        />
        <Select
          disabled={loading && !alarmsStore.alarms.length}
          placeholder="Alarm Status"
          onChange={event => updateSearch(event, 'acknowledged')}
          value={(() => { // NOTE - There might be a better way to handle this
            const value = searchParams.get('acknowledged');
            if (value == 'true') return true;
            if (value == 'false') return false;
            return undefined;
          })()}
          options={[
            {value: true, label: t`Acknowledged`},
            {value: false, label: t`Not Acknowledged`}
          ]}
        />
        <Select
          disabled={loading && !alarmsStore.alarms.length}
          placeholder="Location"
          onChange={event => updateSearch(event, 'locationId')}
          value={searchParams.get('locationId') || undefined}
          options={
            getAllChildDevicesByType(devicesStore.devices.find(d => d.type == 'Site'), 'Location')
              .map(location => {
                return {
                  value: location.id,
                  label: location.name
                }
              })
          }
        />
        <input
          placeholder="Search by device name"
          value={searchParams.get('deviceName') || ''}
          onChange={event => updateSearch(event.target.value || undefined, 'deviceName')}
        />
        <br />
        <h4>Date Range</h4>
        <DateRangePicker
          start={searchParams.get('start')}
          end={searchParams.get('end')}
          time={false}
          onChange={updateDate}
          ref={datePicker}
        />
        <button
          onClick={resetSearch}
          disabled={!searchParams.size}
        >
          <Trans>Reset</Trans>
        </button>
      </div>
      <div className="sub-view">
        <h2><Trans>Alarms</Trans></h2>
        {/* Locked out devices warning  */}
        <Banner status="warning" show={lockedOutDevices.length} key="lock-out-banner">
          <Trans>The following devices are currently locked out and not generating new alarms</Trans>:&nbsp;
          {lockedOutDevices.map((device, i) => {
            return (
              <span key={`locked-out-link-${device.id}`}>
                <Link to={getDeviceUrl(device)}>
                  <strong>{device.model.name}</strong>
                </Link>{device == last(lockedOutDevices) ? '' : ', '}
              </span>
            );
          })}
        </Banner>
        <Controls />
        <RichTable
          loading={loading}
          columns={columns}
          rows={filteredRows}
          allowSelection={true}
          responsive={true}
          rowUniqueKey="alarmId"
          items="alarms"
          onChange={setSelectedRows}
        >
          <AlarmsTableRow acknowledge={acknowledge} acknowledging={acknowledging} />
        </RichTable>
        <Tooltip id="inline-tooltip" />
      </div>
    </div>
  )
}

export default Alarms;
